/**
 * PaymentModal.js
 *
 * Full Razorpay checkout flow for plan upgrade / renewal.
 *
 * Props:
 *   open       — boolean
 *   plan       — { _id, planName, planRate, planValidity }
 *   onClose    — fn()
 *   onSuccess  — fn(updatedUser)   called after plan is activated
 */

import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
    HiOutlineXMark, HiOutlineCheckCircle, HiOutlineArrowPath,
    HiOutlineLockClosed, HiOutlineShieldCheck,
} from "react-icons/hi2"
import API from "../../Common/API"
import { useLanguage } from "../../Common/LanguageContext"

// ── Dynamically load Razorpay checkout script ────────────────────────────────
const loadRazorpay = () =>
    new Promise((resolve) => {
        if (window.Razorpay) { resolve(true); return }
        const script = document.createElement("script")
        script.src = "https://checkout.razorpay.com/v1/checkout.js"
        script.onload  = () => resolve(true)
        script.onerror = () => resolve(false)
        document.body.appendChild(script)
    })
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = { idle: "idle", loading: "loading", paying: "paying", success: "success", error: "error" }

const PaymentModal = ({ open, plan, onClose, onSuccess }) => {
    const { t } = useLanguage()
    const [step,    setStep]    = useState(STEPS.idle)
    const [errMsg,  setErrMsg]  = useState("")
    const rzRef = useRef(null)   // holds the open Razorpay instance so we can close it on unmount

    // Reset state each time the modal opens with a new plan
    useEffect(() => {
        if (open) { setStep(STEPS.idle); setErrMsg("") }
    }, [open, plan?._id])

    // Close any open Razorpay checkout if modal is unmounted / closed
    useEffect(() => {
        return () => { rzRef.current?.close?.() }
    }, [])

    const handlePay = async () => {
        setStep(STEPS.loading)
        setErrMsg("")

        // 1 — Load Razorpay SDK
        const loaded = await loadRazorpay()
        if (!loaded) {
            setErrMsg("Could not load payment gateway. Check your internet connection.")
            setStep(STEPS.error)
            return
        }

        // 2 — Create order on backend
        let orderData
        try {
            const res = await API.post("/payments/createOrder", { planId: plan._id })
            orderData = res.data?.data
        } catch (err) {
            const msg = err?.response?.data?.error || "Could not create payment order."
            // Gateway not configured yet (dev mode)
            if (err?.response?.status === 503) {
                setErrMsg("Payment gateway not configured yet. Add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET to backend/.env and restart the server.")
            } else {
                setErrMsg(msg)
            }
            setStep(STEPS.error)
            return
        }

        // 3 — Open Razorpay checkout
        setStep(STEPS.paying)
        const options = {
            key:         orderData.keyId,
            amount:      orderData.order.amount,          // paise
            currency:    orderData.order.currency || "INR",
            name:        "Royal Kavery Matrimony",
            description: plan.planName,
            order_id:    orderData.order.id,
            prefill: {
                name:    orderData.user.name,
                email:   orderData.user.email,
                contact: orderData.user.mobile,
            },
            notes: {
                planName: plan.planName,
                refId:    orderData.user.refId,
            },
            theme: { color: "#c8603b" },

            // 4 — Payment success → verify on backend → activate plan
            handler: async (response) => {
                setStep(STEPS.loading)
                try {
                    const verifyRes = await API.post("/payments/verifyPayment", {
                        razorpay_order_id:   response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature:  response.razorpay_signature,
                        planId:              plan._id,
                    })
                    const updatedUser = verifyRes.data?.data
                    setStep(STEPS.success)
                    setTimeout(() => {
                        onSuccess?.(updatedUser)
                        onClose?.()
                    }, 2200)
                } catch (err) {
                    setErrMsg(err?.response?.data?.error || "Payment received but verification failed. Contact support with your payment ID: " + response.razorpay_payment_id)
                    setStep(STEPS.error)
                }
            },

            modal: {
                ondismiss: () => {
                    // User closed Razorpay without paying
                    if (step !== STEPS.success) setStep(STEPS.idle)
                },
            },
        }

        rzRef.current = new window.Razorpay(options)
        rzRef.current.on("payment.failed", (response) => {
            setErrMsg(`Payment failed: ${response.error.description}`)
            setStep(STEPS.error)
        })
        rzRef.current.open()
    }

    if (!plan) return null

    const validityDays = plan.planValidity || plan.validityInDays || 30

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={() => { if (step !== STEPS.paying && step !== STEPS.loading) onClose?.() }}
                >
                    <motion.div
                        className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
                        initial={{ scale: 0.93, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 24 }}
                        transition={{ type: "spring", damping: 28, stiffness: 280 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* ── Success state ── */}
                        {step === STEPS.success ? (
                            <div className="flex flex-col items-center gap-3 px-8 py-10 text-center">
                                <motion.div
                                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                                    transition={{ type: "spring", damping: 14, stiffness: 200 }}
                                >
                                    <HiOutlineCheckCircle className="h-16 w-16 text-[#1a8a3a]" />
                                </motion.div>
                                <h3 className="text-xl font-bold text-tColor">Payment Successful!</h3>
                                <p className="text-sm text-slate-500">
                                    <strong>{plan.planName}</strong> is now active.
                                    Your premium features are unlocked.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-borderColor px-5 py-4">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">Membership</p>
                                        <h3 className="text-base font-bold text-tColor">{plan.planName}</h3>
                                    </div>
                                    {step !== STEPS.paying && step !== STEPS.loading && (
                                        <button type="button" onClick={onClose}
                                            className="flex h-8 w-8 items-center justify-center rounded-full border border-borderColor text-slate-400 hover:text-tColor transition">
                                            <HiOutlineXMark className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Body */}
                                <div className="px-5 py-5 space-y-4">
                                    {/* Plan summary card */}
                                    <div className="rounded-2xl bg-primeSoft/40 border border-primeColor/20 p-4 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-tColor">{plan.planName}</span>
                                            <span className="text-xl font-bold text-primeColor">
                                                ₹{(plan.planRate || 0).toLocaleString("en-IN")}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500">Valid for {validityDays} days</p>
                                        <hr className="border-borderColor" />
                                        {/* What's included */}
                                        {[
                                            "Phone & email contact details",
                                            "Full jathagam & rasi details",
                                            "Detailed porutham breakdown",
                                            "Priority listing",
                                        ].map(f => (
                                            <p key={f} className="flex items-center gap-2 text-xs text-slate-600">
                                                <HiOutlineCheckCircle className="h-3.5 w-3.5 shrink-0 text-[#1a8a3a]" />{f}
                                            </p>
                                        ))}
                                    </div>

                                    {/* Secure payment note */}
                                    <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                                        <HiOutlineShieldCheck className="h-3.5 w-3.5" />
                                        Secured by Razorpay · GPay · PhonePe · UPI · Cards
                                    </p>

                                    {/* Error banner */}
                                    {step === STEPS.error && errMsg && (
                                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 leading-relaxed">
                                            {errMsg}
                                        </div>
                                    )}

                                    {/* Pay button */}
                                    <button
                                        type="button"
                                        onClick={handlePay}
                                        disabled={step === STEPS.loading || step === STEPS.paying}
                                        className="w-full flex items-center justify-center gap-2 rounded-2xl bg-primeColor py-3.5 text-sm font-bold text-white hover:bg-primeDark disabled:opacity-60 transition"
                                    >
                                        {(step === STEPS.loading || step === STEPS.paying) ? (
                                            <>
                                                <HiOutlineArrowPath className="h-4 w-4 animate-spin" />
                                                {step === STEPS.loading ? "Preparing…" : "Waiting for payment…"}
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineLockClosed className="h-4 w-4" />
                                                Pay ₹{(plan.planRate || 0).toLocaleString("en-IN")}
                                            </>
                                        )}
                                    </button>

                                    {step === STEPS.error && (
                                        <button type="button" onClick={() => setStep(STEPS.idle)}
                                            className="w-full text-center text-xs text-slate-400 hover:text-tColor underline">
                                            Try again
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default PaymentModal
